import React, { memo, useEffect, useMemo } from "react";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import { toCamelCase } from ".";
import { useAppSelector } from "../../Store";
import { Graph_QL, useAnimal, useSection, AnimalInfo, useMilestones } from "../../Services";
import { MilestoneInfo } from "../../Services/Agrisys_GqlDataCaller/MilestoneDataCalls/GQLCalls/getMilestonesCall";
import ProgressCircle from "../ProgressViewer/progressCircle";
import { UNIT } from "..";
import { colors, glyphs } from "../../Styles";

// Progress circles on feeding
export type NewDefaultProgressCircle = {
  progressCircleId: number;
  header: string;
  unit: JSX.Element;
  progressed: number;
  total: number;
  tooMuchFedDisclaimer: boolean;
};

interface DefaultProgressCirclesProps {
  animals: AnimalInfo[];
  connect: {
    connection: Graph_QL.Connection
    domain: string
  }
}

// #region | Feed Consumption
const FEED_BALANCE = 0.2; //TODO get from settings?

const notFedLastDays = (animalsAtLocations: AnimalInfo[], feedBalance: number = FEED_BALANCE): {total:number, missing:number} => {
  let animalCnt = 0;
  let missingCnt = animalsAtLocations.filter((animal: AnimalInfo) => {
    let consumptions = animal.consumptions ? animal.consumptions : []
    let const_Today = consumptions.find(e => e.milestoneDayOffset !== undefined && e.milestoneDayOffset === 0)
    let const_Yesterday = consumptions.find(e => e.milestoneDayOffset !== undefined && e.milestoneDayOffset === 1)
    

    if (!const_Today || !const_Yesterday) return false
    
    animalCnt++;
    let MissingToday = (const_Today.progressed??0) < (const_Today.total??0) * (1 -feedBalance)
    let MissingYesterday = (const_Yesterday.progressed ?? 0) < (const_Yesterday.total ?? 0) * (1 - feedBalance)
    return MissingToday && MissingYesterday
  }).length;
  return {total:animalCnt, missing:missingCnt}
}

export const underFeedBalanceYesterday = (animals: AnimalInfo[], feedBalance: number = FEED_BALANCE): number => {

  return animals.filter((animal) => {
    if (animal.consumptions === undefined) {
      return false;
    }
    const consumptionYesterday = animal.consumptions.find((consumption) => consumption.milestoneDayOffset === 1);
    
    if (consumptionYesterday === undefined) {
      return false;
    }
    
    if (consumptionYesterday.total === 0 || consumptionYesterday.total === undefined) {
      return false;
    } 

    if (consumptionYesterday.progressed) {
      return ((consumptionYesterday.total-consumptionYesterday.progressed) / consumptionYesterday.total)*100 > feedBalance;
    }

    return false;

  }).length
}
// #endregion

const NewDefaultProgressCircles = ({ animals, connect }: DefaultProgressCirclesProps): NewDefaultProgressCircle[] => {
  const { t } = useTranslation();

  const { call: getMilestoneInfo, data: milestonesState } = useMilestones(connect.connection);

  useEffect(() => {
    getMilestoneInfo({ fetchAmount: 50 });
  }, []);

  const consumptionDay = (animalsAtLocations: AnimalInfo[], dayOffset: number): {AnimalCnt:number, Total: number, Remaning: number } => {
    let {cnt, Total, Remaning } = animalsAtLocations.reduce((accum, animal: AnimalInfo) => {
      let Res_Val = accum;
      let consumptions = animal.consumptions ? animal.consumptions : []
      let const_Day = consumptions.find(e => e.milestoneDayOffset!==undefined && e.milestoneDayOffset === dayOffset)
      if (!const_Day) return Res_Val
      Res_Val.cnt++;
      Res_Val.Total = Res_Val.Total + ((const_Day.total??0) / 1000)
      Res_Val.Remaning = Res_Val.Remaning + ((const_Day.progressed??0) / 1000)
      return Res_Val
    }, {cnt:0, Total: 0, Remaning: 0 });
    return {AnimalCnt:cnt, Total, Remaning }
  }


  // #region | Milestones
  const milestones = useMemo(() => {
    let result: MilestoneInfo[] = [];
    if (milestonesState && milestonesState[connect.domain]) {
      result = milestonesState[connect.domain].data;
    }
    return result;
  }, [connect.domain, milestonesState]);
  // #endregion

  const calculatedProgressCircles = useMemo(() => {
    let result: NewDefaultProgressCircle[] = []
    
    let TodayCons = consumptionDay(animals, 0)
    let YesterdayCons = consumptionDay(animals, 1)
    let notFeed = notFedLastDays(animals)

    let baseCircles: NewDefaultProgressCircle[] = [
      {
        progressCircleId: 1,
        header: t("feed.general.remainingToday", {
          defaultValue: "remaining today",
        }),
        unit: <UNIT content={"Kg"} color={colors.greyPrimary} glyph={<glyphs.Kg size="15px" fillColor={colors.greyPrimary}/>}/>,
        progressed: +(TodayCons.Total - TodayCons.Remaning).toFixed(2),
        total: +TodayCons.Total.toFixed(2),
        tooMuchFedDisclaimer: true
      },
      {
        progressCircleId: 2,
        header: t("feed.general.remainingYesterday", {
          defaultValue: "remaining yesterday",
        }),
        unit: <UNIT content={"Kg"} color={colors.greyPrimary} glyph={<glyphs.Kg size="15px" fillColor={colors.greyPrimary}/>}/>,
        progressed: +(YesterdayCons.Total - YesterdayCons.Remaning).toFixed(2),
        total: +YesterdayCons.Total.toFixed(2),
        tooMuchFedDisclaimer: true
      },
      {
        progressCircleId: 3,
        header: t("feed.general.waterUse", { defaultValue: "water use today" }),
        unit: <UNIT content={"Kg"} color={colors.greyPrimary} glyph={<glyphs.Kg size="15px" fillColor={colors.greyPrimary}/>}/>,
        progressed: +(TodayCons.Total - TodayCons.Remaning).toFixed(2),
        total: +TodayCons.Total.toFixed(2),
        tooMuchFedDisclaimer: false
      },
      {
        progressCircleId: 4,
        header: t("feed.general.notFed", { defaultValue: "fed <20% last two days" }),
        unit: <UNIT content={"Animals"} color={colors.greyPrimary}/>,
        progressed: notFeed.missing,
        total: notFeed.total,
        tooMuchFedDisclaimer: false
      },
    ]
    result = result.concat(baseCircles)
    
    let milestoneCircles: NewDefaultProgressCircle[] =  milestones.map((milestone, index) => {
      const milestoneKey = toCamelCase(milestone.name);
      return {
        progressCircleId: 5 + index,
        header: t(`milestones.defaultMilestones.${milestoneKey}`, {
          defaultValue: milestone.name,
        }),
        unit: <UNIT content={"Animals"} color={colors.greyPrimary}/>,
        progressed:
        animals.filter(
            (animal: AnimalInfo) =>
              animal.milestone?.milestone_id === milestone.milestone_id
          ).length ?? 0,
        total: animals.length,
        tooMuchFedDisclaimer: false
      };
    })
    result = result.concat(milestoneCircles)
    return result
  },[animals, milestones])

  return calculatedProgressCircles;
};

export default NewDefaultProgressCircles;

export const NewProgressCicle = memo(({ animals, circleId, connect }: { animals: AnimalInfo[], circleId: number, connect: {connection: Graph_QL.Connection, domain: string} }) => {
  const defaultProgressCircles = NewDefaultProgressCircles({ animals, connect });
  let circleData = defaultProgressCircles.find(c => c.progressCircleId === circleId)
  if (!circleData) return;
  
  return (
    <ProgressCircle
      animation
      key={circleData.progressCircleId}
      header={circleData.header}
      unit={circleData.unit}
      progressed={circleData.progressed}
      total={circleData.total} 
      tooMuchFedDisclaimer={circleData.tooMuchFedDisclaimer}
  />
  )
});